// src/pages/PerformanceNewReportPage.jsx
import React, { useMemo, useState, useEffect } from 'react';
import TimeZoneDropdown from './components/TimeZoneDropdown';
import ReportTabs from './components/tabs/ReportTabs';


const reportTypes = [
  'At Sea',
  'In Port',
  'Anchor',
  'Drifting',
  'Harbour Streaming',
  'Drydock',
  'Layup'
];

const PerformanceNewReportPage = () => {
  // Header / Report No
  const [reportNo] = useState(1);
  const createdAt = useMemo(
    () =>
      new Date().toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
    []
  );

  // Ship list + selection
  const [ships, setShips] = useState([]);
  const [selectedShip, setSelectedShip] = useState('');
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ships`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setShips)
      .catch(err => {
        console.error('Error fetching ships:', err);
        setShips([]);
      });
  }, []);

  // Report Type selection
  const [reportType, setReportType] = useState('');

  // Full fields from template
  const [fullFields, setFullFields] = useState([]);

  // Filtered & grouped by section according to reportType
  const [templateMapping, setTemplateMapping] = useState({});

  // Fetch operational-template for selected ship
  useEffect(() => {
    if (!selectedShip) {
      setFullFields([]);
      return;
    }
    (async () => {
      try {
        // 1) fetch template‐map for vessel
        const mapRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/template-maps?vesselId=${selectedShip}`
        );
        if (!mapRes.ok) throw new Error(mapRes.statusText);
        const mapArr = await mapRes.json();
        const mapObj = mapArr.find(m => m.vessel?._id === selectedShip);
        if (!mapObj) throw new Error('No template map for this vessel');
        const tmplId = mapObj.mappings?.operational?._id;
        if (!tmplId) throw new Error('No operational template assigned');

        // 2) fetch all operational templates, then pick mine
        const allRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational`);
        if (!allRes.ok) throw new Error(allRes.statusText);
        const allTemplates = await allRes.json();
        const template = allTemplates.find(t => t._id === tmplId);
        if (!template) throw new Error('Operational template not found');

        setFullFields(template.fields || []);
      } catch (err) {
        console.error('Error loading template fields:', err);
        setFullFields([]);
      }
    })();
  }, [selectedShip]);

  // Whenever fullFields or reportType changes, filter & group
  useEffect(() => {
    if (!reportType) {
      setTemplateMapping({});
      return;
    }
    const filtered = fullFields.filter(f => f.reportType === reportType);
    const grouped = filtered.reduce((acc, f) => {
      acc[f.section] = acc[f.section] || [];
      acc[f.section].push(f);
      return acc;
    }, {});
    setTemplateMapping(grouped);
  }, [fullFields, reportType]);

  // Report Information state
  const [voyageNo, setVoyageNo] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [timeZone, setTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [prevTimeZone, setPrevTimeZone] = useState('');
  const [reportPeriod, setReportPeriod] = useState('');

  // Calculate reportPeriod in hours.decimal
  useEffect(() => {
    if (periodFrom && periodTo) {
      const diffMs = new Date(periodTo) - new Date(periodFrom);
      setReportPeriod(
        isNaN(diffMs) || diffMs < 0
          ? ''
          : (diffMs / (1000 * 60 * 60)).toFixed(2)
      );
    } else {
      setReportPeriod('');
    }
  }, [periodFrom, periodTo]);

  return (
    <div className="container-fluid px-0 py-4">
      {/* Header */}
      <h2 className="mb-1">Performance Report No : {reportNo}</h2>
      <p className="text-muted mb-4">
        <strong>Created At :</strong> {createdAt}
      </p>

      {/* Report Type & Ship */}
      <div className="row gx-3 gy-3 mb-4">
        <div className="col-md-3">
          <label htmlFor="reportType" className="form-label fw-bold">
            Select report Type :
          </label>
          <select
            id="reportType"
            className="form-select"
            value={reportType}
            onChange={e => setReportType(e.target.value)}
          >
            <option value="">Select report type</option>
            {reportTypes.map(rt => (
              <option key={rt} value={rt}>{rt}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label htmlFor="shipSelect" className="form-label fw-bold">
            Select Ship :
          </label>
          <select
            id="shipSelect"
            className="form-select"
            value={selectedShip}
            onChange={e => setSelectedShip(e.target.value)}
          >
            <option value="">Select ship</option>
            {ships.map(ship => (
              <option key={ship._id} value={ship._id}>
                {ship.vesselName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Report Information */}
      <div className="card w-100 mb-4">
        <div className="card-header bg-light fs-4 fw-bold">
          Report Information
        </div>
        <div className="card-body">
          <div className="row gx-3 gy-3">
            <div className="col-md-3">
              <label className="form-label fw-bold">Report No :</label>
              <input
                type="number"
                className="form-control"
                value={reportNo}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Voyage No :</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter voyage number"
                value={voyageNo}
                onChange={e => setVoyageNo(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Period From :</label>
              <input
                type="datetime-local"
                className="form-control"
                value={periodFrom}
                onChange={e => setPeriodFrom(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Period To :</label>
              <input
                type="datetime-local"
                className="form-control"
                value={periodTo}
                onChange={e => setPeriodTo(e.target.value)}
              />
            </div>

            <TimeZoneDropdown
              id="timeZone"
              label="Time Zone :"
              value={timeZone}
              onChange={e => setTimeZone(e.target.value)}
              className="col-md-3"
            />
            <TimeZoneDropdown
              id="prevTimeZone"
              label="Previous Time Zone :"
              value={prevTimeZone}
              onChange={e => setPrevTimeZone(e.target.value)}
              className="col-md-3"
            />

            <div className="col-md-3">
              <label className="form-label fw-bold">Report Period (hrs) :</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={reportPeriod}
                readOnly
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Tabs showing fields filtered by reportType */}
      <ReportTabs
        reportType={reportType}
        fieldsBySection={templateMapping}
      />
    </div>
  );
};

export default PerformanceNewReportPage;